const post = {
  slug: 'youtube-search-vs-browse-traffic',
  cluster: 'topics',
  eyebrow: 'Topics',
  title: 'Search traffic and suggested traffic want different videos',
  description:
    'The two main ways people find a video reward almost opposite things. Knowing which one you are making for changes the title, the topic and the opening.',
  keywords: [
    'youtube search traffic', 'youtube suggested videos', 'browse features',
    'youtube traffic sources', 'youtube seo', 'how youtube recommends videos',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/youtube-search-vs-browse-traffic.webp',
    alt: 'Two paths to one video: a typed query on one side, a recommendation sidebar on the other.',
  },
  lede:
    'A video optimised for search and a video optimised for suggestion are not the same video. Most advice blends them, which is why so much of it contradicts itself.',
  sections: [
    {
      heading: 'The two paths',
      body: [
        {
          type: 'para',
          text: '**Search**: someone types a question and picks from results. They have a specific intent and will leave the moment it is clear you are not answering it.',
        },
        {
          type: 'para',
          text: '**Suggested and browse**: the video is placed in front of someone who was not looking for it. They have no intent, and the only question is whether this looks more interesting than the alternatives on screen.',
        },
        {
          type: 'para',
          text: 'Those are different psychological situations, and they reward different choices at almost every level of the video.',
        },
      ],
    },
    {
      heading: 'What search rewards',
      body: [
        {
          type: 'list',
          items: [
            '**A title matching how people phrase the question**, not how you would phrase it. Clever titles lose here.',
            '**Answering early enough to prove you will answer.** A search viewer needs confirmation they are in the right place; withholding too long reads as the wrong video rather than as suspense.',
            '**Topics with durable demand.** Search traffic accumulates, which is why evergreen and search go together naturally.',
            '**Precision over breadth.** The narrow question has less competition and a viewer who wants exactly it.',
          ],
        },
      ],
    },
    {
      heading: 'What suggestion rewards',
      body: [
        {
          type: 'list',
          items: [
            '**Packaging that creates curiosity in someone who had none.** The tension in the title matters far more here than keyword phrasing.',
            '**A strong hook**, because there is no pre-existing intent holding them.',
            '**Session behaviour.** Suggestion favours videos that keep people watching afterwards, so being recognisably part of a series helps.',
            '**Broad appeal within the topic.** A very narrow question has a small pool of people who would be interested unprompted.',
          ],
        },
        {
          type: 'para',
          text: 'Notice that two of these are the direct opposite of the search list. That is the tension, and it is real rather than an artefact of bad advice.',
        },
      ],
    },
    {
      heading: 'Which one to build for',
      body: [
        {
          type: 'para',
          text: 'For a new faceless channel, **search first** — for one practical reason. Search does not require the system to already trust your channel. Someone typed the question; you either match it or you do not. Suggestion requires the platform to decide you are worth putting in front of people who did not ask, and it has little basis for that early.',
        },
        {
          type: 'para',
          text: 'Suggested traffic then tends to follow once there are enough videos with good retention for the system to have something to work with. Building for suggestion from a standing start is the harder path.',
        },
        {
          type: 'callout',
          title: 'Check your own mix',
          text: 'Analytics reports traffic sources per video. If a video you assumed was a search video is getting most of its views from suggestion, that is real information about what it actually is — and it should change the next title.',
        },
      ],
    },
    {
      heading: 'Serving both without splitting the difference',
      body: [
        {
          type: 'para',
          text: 'The compromise that fails is a title half-optimised for each: neither phrased as the question nor interesting to a stranger.',
        },
        {
          type: 'para',
          text: 'What works better is a title carrying the searchable phrase *and* a tension, with the thumbnail doing the curiosity work for the browse audience. That is the pairing described in [title and thumbnail as one unit](/blogs/youtube-title-and-thumbnail).',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'Do tags matter?',
      a: 'Very little, by most accounts, and far less than title, thumbnail and the first lines of the description. Filling them is cheap; treating them as a lever is misplaced effort.',
    },
    {
      q: 'Does the description affect search?',
      a: 'The opening lines are the part worth writing carefully — they are used for context and are visible to viewers. A wall of keywords at the bottom is not doing what people hope.',
    },
  ],
  related: ['evergreen-vs-trending-youtube', 'youtube-title-and-thumbnail', 'how-to-evaluate-a-youtube-niche'],
};

export default post;
